import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { money, monthLabel } from "../lib/format";
import { Panel, Empty, StatCard } from "./Widgets";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export function ForecastPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/forecast").then((r) => setD(r.data)); }, []);
  if (!d || !d.month) return null;
  const rows = d.agents.filter((a) => a.basis_months > 0);
  return (
    <Panel title={`Forecast · ${monthLabel(d.month)}`} className="mb-6" action={<span className="text-xs font-mono text-slate-500" data-testid="forecast-total">≈ {money(d.total)} total · based on {d.based_on.length} month{d.based_on.length === 1 ? "" : "s"}</span>}>
      {rows.length === 0 ? <Empty>No agent history to forecast from yet.</Empty> : (
        <div className="overflow-x-auto">
          <table className="tbl w-full" data-testid="forecast-table">
            <thead><tr><th>Agent</th>{d.based_on.map((m) => <th key={m} className="!text-right">{monthLabel(m)}</th>)}<th className="!text-right">Forecast</th><th className="!text-right">Range</th><th>Confidence</th></tr></thead>
            <tbody>{rows.map((a) => (
              <tr key={a.agent_id} data-testid={`forecast-${a.agent_code}`}>
                <td>{a.name}</td>
                {a.history.map((h) => <td key={h.month} className="text-right font-mono text-slate-400">{money(h.payout)}</td>)}
                <td className="text-right font-mono text-emerald-400 font-semibold">{money(a.forecast)}</td>
                <td className="text-right font-mono text-xs text-slate-500">{money(a.low)} – {money(a.high)}</td>
                <td><span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${a.confidence === "high" ? "text-emerald-400 border-emerald-500/40" : "text-amber-300 border-amber-500/40"}`}>{a.confidence}</span></td>
              </tr>
            ))}</tbody>
          </table>
          <div className="px-5 py-3 text-xs text-slate-500">Weighted average of the last 3 months (most recent counts most). Estimate only — actual residuals depend on merchant activity.</div>
        </div>
      )}
    </Panel>
  );
}

export function ForecastCard({ agentId }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    api.get("/forecast", { params: agentId ? { agent_id: agentId } : {} }).then((r) => setF({ month: r.data.month, ...(r.data.agents[0] || {}) }));
  }, [agentId]);
  if (!f?.month || !f.basis_months) return null;
  return <StatCard label={`Forecast · ${monthLabel(f.month)}`} value={money(f.forecast)} hint={<>{f.confidence} confidence · range <span>{money(f.low)}</span>–<span>{money(f.high)}</span></>} testId="agent-forecast" delay={240} />;
}

export function BulkReassignDialog({ open, onClose, merchantIds, onDone }) {
  const [agents, setAgents] = useState([]);
  const [house, setHouse] = useState([]);
  const [months, setMonths] = useState([]);
  const [target, setTarget] = useState("");
  const [from, setFrom] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/agents").then((r) => setAgents(r.data));
    api.get("/settings").then((r) => setHouse(r.data.house_codes));
    api.get("/reports").then((r) => { const ms = [...new Set(r.data.map((x) => x.month))].sort().reverse(); setMonths(ms); setFrom(ms[0] || ""); });
    setTarget("");
  }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/merchants/reassign-bulk", { merchant_ids: merchantIds, target, from_month: from });
      toast.success(`Moved ${data.moved} merchant${data.moved === 1 ? "" : "s"} to ${data.to_owner}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#131B2E] border-slate-800" data-testid="bulk-reassign-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><TrendingUp size={16} className="text-blue-300" />Move {merchantIds.length} merchant{merchantIds.length === 1 ? "" : "s"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><div className="text-xs text-slate-400 mb-1.5">Move to</div>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="bulk-reassign-target" className="bg-[#0B0F17]"><SelectValue placeholder="Choose owner…" /></SelectTrigger>
              <SelectContent>
                {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.agent_code})</SelectItem>)}
                {house.map((h) => <SelectItem key={h} value={`house:${h}`}>House ({h})</SelectItem>)}
                <SelectItem value="sheet">Follow sheet code again</SelectItem>
              </SelectContent>
            </Select></div>
          <div><div className="text-xs text-slate-400 mb-1.5">Starting month</div>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger data-testid="bulk-reassign-month" className="bg-[#0B0F17] font-mono"><SelectValue /></SelectTrigger>
              <SelectContent>{months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
            </Select></div>
          <p className="text-xs text-slate-500">Earlier months stay with their current owner. Future uploads follow this choice. All payouts are recalculated.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="bulk-reassign-submit" disabled={!target || !from || busy} onClick={submit} className="bg-blue-500 hover:bg-blue-600 text-white">{busy ? "Moving…" : "Move merchants"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
